import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { BoxProps, Chip, useTheme } from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { SparklesIcon } from "@/Components/Icons/SparklesIcons";
import { Box } from "@/Components/v2/atoms/Box";
import { Divider } from "@/Components/v2/atoms/Divider";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Collapse } from "@/Components/v2/atoms/Transitions";
import { Typography } from "@/Components/v2/atoms/Typography";
import { WordByWordLoader } from "@/Components/v2/atoms/WordByWordLoader/WordByWordLoader";
import InsightCard from "@/Components/v2/molecule/InsightCard/InsightCard";
import { brandColors } from "@/Components/v2/utils/brand-colors";
import { useAnalytics } from "@/hooks/useAnalytics";
import { InstantInsightsResult } from "@/pages/channel-roi/api/getChannelROIInsights";

import { instantInsightsTitle } from "./constants";

type InstantInsightsProps = {
  boxProps: BoxProps;
  insightsData:
    | {
        result: InstantInsightsResult | undefined;
        error: string | undefined;
      }
    | undefined;
  isLoading?: boolean;
  startDate: string;
  endDate: string;
  error?: string;
};

const InstantInsightsContainer = ({
  boxProps,
  insightsData,
  isLoading = false,
  startDate,
  endDate,
  error,
}: InstantInsightsProps) => {
  const insights = insightsData?.result?.insights ?? [];
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const hasInsights = insights.length > 0;
  const insightsEmpty = isEmpty(insights) && !error;
  const { buttonClicked } = useAnalytics();

  // Start closed
  const [isExpanded, setIsExpanded] = useState(false);

  // Only allow toggling after insights are ready
  const canToggle = !isLoading && (hasInsights || error);

  // Expand automatically when insights load or when there's an error
  useEffect(() => {
    if (isLoading) {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  }, [isLoading]);

  return (
    <Box
      bgcolor={theme.palette.instantInsights.background}
      borderRadius={3}
      padding={3}
      {...boxProps}
    >
      <Stack
        gap={1.5}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => {
          if (canToggle) setIsExpanded((prev) => !prev);
        }}
        sx={{ cursor: canToggle ? "pointer" : "default" }}
      >
        <Stack gap={1.5} direction="row" alignItems="center">
          <SparklesIcon />
          <Typography variant="h3" fontSize={20} fontWeight={600}>
            {instantInsightsTitle}
          </Typography>
          <Chip
            label="Beta"
            color="primary"
            sx={{
              backgroundColor: isDarkMode ? brandColors.ceasefireWhite : brandColors.pirateBlack,
              color: isDarkMode ? brandColors.pirateBlack : brandColors.ceasefireWhite,
              fontWeight: 600,
              fontSize: "14px",
              height: "24px",
              borderRadius: "16px",
            }}
          />
        </Stack>
        {isLoading ? (
          <WordByWordLoader words={["Generating", "insights..."]} />
        ) : (
          <Box>
            {isExpanded ? (
              <KeyboardArrowUp fontSize="large" />
            ) : (
              <KeyboardArrowDown fontSize="large" />
            )}
          </Box>
        )}
      </Stack>

      <Collapse
        in={isExpanded}
        timeout={{ enter: 500, exit: 400 }}
        easing={{ enter: "cubic-bezier(0.4, 0, 0.2, 1)", exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
      >
        <Divider color={theme.palette.mutinexMagenta.main} sx={{ my: 1.5, opacity: 0.7 }} />
        <Box>
          {error || insightsEmpty ? (
            <Typography mb={2} textAlign="center" variant="body2" color="error">
              There was an issue loading Instant Insights. Please refresh the page or try again
              later.
            </Typography>
          ) : (
            <>
              <Typography mb={2} textAlign="center" variant="body2" fontWeight={600}>
                Analysis of channel performance for {startDate} to {endDate}:
              </Typography>
              <Stack mb={2} direction="row" gap={2}>
                {insights.map((insight) => (
                  <InsightCard key={insight.headline} insight={insight} />
                ))}
              </Stack>
              <Typography mb={2} textAlign="center" variant="body2" fontWeight={600}>
                Want to learn more?{" "}
                <Link
                  to={"/maite"}
                  color={theme.palette.internalLink.color}
                  style={{ textDecoration: "underline" }}
                  onClick={() => {
                    buttonClicked({
                      buttonName: "Ask MAITE a question",
                      buttonLocation: "Instant Insights",
                      buttonType: "cta",
                      additionalProperties: {
                        dataType: "Channel ROI data",
                        insightsAvailable: insights.length,
                      },
                    });
                  }}
                >
                  Ask MAITE a question about your Channel ROI data.
                </Link>
              </Typography>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default InstantInsightsContainer;
